import React from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { cn } from 'lib/utils';
import { useExamInsights } from '../context';
import { UnlockButton } from './UnlockButton';

interface LockedInsightProps {
  /** What is behind the lock, e.g. "Why this answer is right". */
  title: string;
  /** One honest line on what unlocking shows. */
  description: string;
  /** Placeholder line count: the shape of the hidden content, not the content. */
  lines?: number;
  className?: string;
}

/* Fixed widths so the placeholder looks like prose and never changes between renders. */
const LINE_WIDTHS = ['92%', '78%', '86%', '64%', '81%', '70%'];

/**
 * A locked part of an IELTS/SAT result: a blurred placeholder under a frosted panel with a lock chip and
 * the gradient upgrade button. The API sends nothing to blur, so there is nothing here to read in the
 * browser's tools.
 */
export function LockedInsight({ title, description, lines = 3, className }: LockedInsightProps) {
  const { t } = useTranslation();
  const { requestUnlock, unlocking } = useExamInsights();

  return (
    <section
      aria-label={title}
      className={cn('relative mt-5 overflow-hidden rounded-[22px] border border-border bg-card', className)}
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 select-none space-y-2.5 overflow-hidden p-6 opacity-90 blur-[6px]">
        {LINE_WIDTHS.slice(0, Math.max(1, Math.min(lines, LINE_WIDTHS.length))).map((width, i) => (
          <div key={i} className="h-2.5 rounded-full bg-gray-200 dark:bg-white/15" style={{ width }} />
        ))}
      </div>
      <div className="relative flex min-h-[148px] flex-col items-center justify-center gap-2.5 bg-gradient-to-b from-white/40 to-white/90 px-5 py-7 text-center dark:from-gray-950/40 dark:to-gray-950/90">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[linear-gradient(135deg,#4F2BE0,#DB2777)] text-white">
          <Lock className="h-[18px] w-[18px]" aria-hidden />
        </span>
        <p className="font-display text-[17px] font-bold tracking-[-0.02em] text-foreground">{title}</p>
        <p className="max-w-[38ch] text-[13px] leading-snug text-muted-foreground">{description}</p>
        <UnlockButton onClick={requestUnlock} loading={unlocking}>
          {t('exam_insights.unlock')}
        </UnlockButton>
      </div>
    </section>
  );
}
